import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import BrowserOnly from 'components/consumer/BrowserOnly';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import JBImage from 'components/essentials/JBImage';
import PDPDesktopFullScreenGallery2 from 'components/consumer/PDP/PDPDesktopFullScreenGallery2';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import Placeholder from 'components/consumer/Placeholder';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useScreen from 'hooks/useScreen';
import getImageUrlWithoutQuery from 'commons/getImageUrlWithoutQuery';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import BarbieLogo from 'assets/images/made-for-barbie-logo.png';
import BipocLogo from 'assets/images/bipoc-badge.png';
import useIsBrowser from 'hooks/useIsBrowser';
import useBrokenImageUrls from 'global-state/brokenImageUrls/useBrokenImageUrls';
import PDPDesktopThumbnails from './PDPDesktopThumbnails';
import { getMainImageImgixPropsForPhoto } from './helpers/imgix-props';
import usePDPDesktopHeroSizeProps from './helpers/usePDPDesktopHeroSizeProps';
import PDPProductIntroVideo from './PDPProductIntroVideo';
import { PDP_MAIN_IMAGE } from './specs/testIds';
import LinkOrFragment from '../../LinkOrFragment';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
const QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH = '100%';
const QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH = 570;

// Cap the SSR/first-paint main image width. The server renders a plain <img>
// (no srcset), and React Float preloads that exact URL as the LCP candidate.
// Without a width, imgix serves the source at native resolution, so an oversized
// source (e.g. the bondi-outdoor-double-sofa config image at 7366px / ~8MB)
// becomes a multi-second LCP. 1600px matches the studio hero default width and
// covers retina; the client react-imgix render upgrades via srcset after hydration.
const SSR_MAIN_IMAGE_MAX_WIDTH = 1600;
const dazeyLogo = 'https://joybird2.imgix.net/user-uploads-staging/JBxDaniDazey-Logo-Button.png';
const PDPDesktopHeroGalleryUI = t0 => {
  const $ = _c(163);
  const {
    campaign,
    configurationPhotos: t1,
    isMechanicalOpen: t2,
    toggleMechanicalOpen,
    cylindoFeatures,
    dimensionsImage,
    enableCrops: t3,
    introVideoIds,
    isConfigFlipped: t4,
    isCylindoAvailable,
    isCylindoConfigurationPhotos,
    isEditOrder: t5,
    isVideoSelected,
    loading: t6,
    mainPhotoLink,
    mode: t7,
    pdpIsModularBYOProduct: t8,
    productName,
    selectedPhoto,
    setIsVideoSelected,
    setSelectedPhoto,
    showDecideLaterOverlay,
    studioPhotos: t9,
    viewGalleryImageEvent,
    dimensionShotUrls: t10,
    currentDimensionShot,
    hasMultipleDimensionShots: t11,
    selectedDimensionShotIndex: t12,
    goToNextDimensionShot,
    goToPreviousDimensionShot,
    shouldShowDimensionThumbnail: t13
  } = t0;
  let t14;
  if ($[0] !== t1) {
    t14 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t14;
  } else {
    t14 = $[1];
  }
  const configurationPhotos = t14;
  const isMechanicalOpen = t2 === undefined ? null : t2;
  const enableCrops = t3 === undefined ? true : t3;
  const isConfigFlipped = t4 === undefined ? false : t4;
  const isEditOrder = t5 === undefined ? true : t5;
  const loading = t6 === undefined ? false : t6;
  const mode = t7 === undefined ? "pdp" : t7;
  const pdpIsModularBYOProduct = t8 === undefined ? false : t8;
  let t15;
  if ($[2] !== t9) {
    t15 = t9 === undefined ? [] : t9;
    $[2] = t9;
    $[3] = t15;
  } else {
    t15 = $[3];
  }
  const studioPhotos = t15;
  let t16;
  if ($[4] !== t10) {
    t16 = t10 === undefined ? [] : t10;
    $[4] = t10;
    $[5] = t16;
  } else {
    t16 = $[5];
  }
  const dimensionShotUrls = t16;
  const hasMultipleDimensionShots = t11 === undefined ? false : t11;
  const selectedDimensionShotIndex = t12 === undefined ? 0 : t12;
  const shouldShowDimensionThumbnail = t13 === undefined ? false : t13;
  const {
    isMobile,
    isMobileOrTablet,
    isDesktop
  } = useScreen();
  const isQuickviewMode = mode === "quickview";
  const isPdpMode = mode === "pdp";
  const isBrowser = useIsBrowser();
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const brokenImageUrls = useBrokenImageUrls();
  const isStudioGallery = selectedPhoto?.type === "studio";
  const isDimensionImg = selectedPhoto?.type === "dimensional";
  const isConfigGallery = selectedPhoto?.type === "configuration" || selectedPhoto?.type === "cylindo";
  const isBuildYourOwnImg = selectedPhoto?.type === "buildyourown";
  let t17;
  if ($[6] !== configurationPhotos || $[7] !== isBuildYourOwnImg || $[8] !== isStudioGallery || $[9] !== pdpIsModularBYOProduct || $[10] !== selectedPhoto || $[11] !== studioPhotos) {
    t17 = isStudioGallery ? studioPhotos : pdpIsModularBYOProduct && isBuildYourOwnImg ? [selectedPhoto] : configurationPhotos;
    $[6] = configurationPhotos;
    $[7] = isBuildYourOwnImg;
    $[8] = isStudioGallery;
    $[9] = pdpIsModularBYOProduct;
    $[10] = selectedPhoto;
    $[11] = studioPhotos;
    $[12] = t17;
  } else {
    t17 = $[12];
  }
  const allPhotos = t17;
  let t18;
  if ($[13] !== allPhotos || $[14] !== brokenImageUrls) {
    let t19;
    if ($[16] !== brokenImageUrls) {
      t19 = t20 => {
        const {
          src
        } = t20;
        const cleanImageUrl = getImageUrlWithoutQuery(src);
        return !brokenImageUrls.includes(cleanImageUrl);
      };
      $[16] = brokenImageUrls;
      $[17] = t19;
    } else {
      t19 = $[17];
    }
    t18 = allPhotos.filter(t19);
    $[13] = allPhotos;
    $[14] = brokenImageUrls;
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  const validPhotos = t18;
  const photos = validPhotos;
  let t19;
  if ($[18] !== photos || $[19] !== selectedPhoto?.src) {
    let t20;
    if ($[21] !== selectedPhoto?.src) {
      t20 = t21 => {
        const {
          src: src_0
        } = t21;
        return src_0 === selectedPhoto?.src;
      };
      $[21] = selectedPhoto?.src;
      $[22] = t20;
    } else {
      t20 = $[22];
    }
    t19 = photos.findIndex(t20);
    $[18] = photos;
    $[19] = selectedPhoto?.src;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  const selectedPhotoIndex = Math.max(0, t19);
  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();
  let t20;
  bb0: {
    if (!selectedPhoto?.src) {
      t20 = null;
      break bb0;
    }
    let t21;
    if ($[23] !== enableCrops || $[24] !== isMobileOrTablet || $[25] !== mainPhotoSizeProps || $[26] !== selectedPhoto) {
      t21 = convertImgixPropsToQueryParams({
        width: SSR_MAIN_IMAGE_MAX_WIDTH,
        ...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops),
        ...mainPhotoSizeProps
      }, selectedPhoto.src, isMobileOrTablet);
      $[23] = enableCrops;
      $[24] = isMobileOrTablet;
      $[25] = mainPhotoSizeProps;
      $[26] = selectedPhoto;
      $[27] = t21;
    } else {
      t21 = $[27];
    }
    t20 = t21;
  }
  const ssrMainImageSrc = t20;
  const introVideoId = introVideoIds && (isMobile ? introVideoIds.mobile : introVideoIds.desktop);
  const ratioForCroppedImages = isMobileOrTablet ? 1 : 0.6666666666666666;
  const ratio = loading || enableCrops ? ratioForCroppedImages : 0.5;
  const spaceAround = 194;
  let t21;
  bb1: {
    if (isQuickviewMode) {
      t21 = QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
      break bb1;
    }
    t21 = `calc((100vh - ${194}px) / ${ratio})`;
  }
  const maxGalleryWidth = t21;
  let t22;
  bb2: {
    if (isQuickviewMode && !isDesktop) {
      t22 = QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH;
      break bb2;
    }
    if (isQuickviewMode) {
      t22 = QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
      break bb2;
    }
    t22 = undefined;
  }
  const galleryWidth = t22;
  let t23;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      setIsFullScreenGalleryOpen(true);
    };
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  const openGalleryModal = t23;
  let t24;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setIsFullScreenGalleryOpen(false);
    };
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  const closeGalleryModal = t24;
  const isMainPhotoLinked = isQuickviewMode && mainPhotoLink;
  let t25;
  if ($[30] !== galleryWidth || $[31] !== maxGalleryWidth) {
    t25 = {
      maxWidth: maxGalleryWidth,
      width: galleryWidth
    };
    $[30] = galleryWidth;
    $[31] = maxGalleryWidth;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  const t26 = isMainPhotoLinked ? "block focus-visible:outline-offset-4" : undefined;
  const t27 = isMainPhotoLinked ? mainPhotoLink : undefined;
  const t28 = mode === "pdp";
  const t29 = mode === "quickview";
  let t30;
  if ($[33] !== brokenImageUrls || $[34] !== selectedPhoto?.src || $[35] !== t28 || $[36] !== t29) {
    t30 = classnames("bg-transparent border-0 focus:outline-0", {
      "PDPDesktopHeroGalleryUI__MainPhoto--mode-pdp": t28,
      "PDPDesktopHeroGalleryUI__MainPhoto--mode-quickview": t29,
      "flex justify-center": brokenImageUrls.includes(selectedPhoto?.src)
    });
    $[33] = brokenImageUrls;
    $[34] = selectedPhoto?.src;
    $[35] = t28;
    $[36] = t29;
    $[37] = t30;
  } else {
    t30 = $[37];
  }
  let t31;
  if ($[38] !== isConfigGallery || $[39] !== isCylindoConfigurationPhotos || $[40] !== isPdpMode || $[41] !== isStudioGallery || $[42] !== isVideoSelected) {
    t31 = () => {
      if (isPdpMode && (!isCylindoConfigurationPhotos && isConfigGallery || isStudioGallery) && !isVideoSelected) {
        openGalleryModal();
      }
    };
    $[38] = isConfigGallery;
    $[39] = isCylindoConfigurationPhotos;
    $[40] = isPdpMode;
    $[41] = isStudioGallery;
    $[42] = isVideoSelected;
    $[43] = t31;
  } else {
    t31 = $[43];
  }
  let t32;
  if ($[44] !== currentDimensionShot || $[45] !== dimensionShotUrls || $[46] !== enableCrops || $[47] !== goToNextDimensionShot || $[48] !== goToPreviousDimensionShot || $[49] !== hasMultipleDimensionShots || $[50] !== isBrowser || $[51] !== isConfigFlipped || $[52] !== isCylindoAvailable || $[53] !== isCylindoConfigurationPhotos || $[54] !== isDimensionImg || $[55] !== isStudioGallery || $[56] !== isVideoSelected || $[57] !== loading || $[58] !== mainPhotoSizeProps || $[59] !== mode || $[60] !== productName || $[61] !== selectedDimensionShotIndex || $[62] !== selectedPhoto || $[63] !== ssrMainImageSrc) {
    t32 = !loading && (isCylindoAvailable && isCylindoConfigurationPhotos ? isStudioGallery || isDimensionImg : true) && !isVideoSelected && !!selectedPhoto && (!!selectedPhoto.src || isDimensionImg) && (isDimensionImg ? <PDPDimensionShotViewer currentDimensionShot={currentDimensionShot} hasMultipleDimensionShots={hasMultipleDimensionShots} selectedDimensionShotIndex={selectedDimensionShotIndex} totalDimensionShots={dimensionShotUrls.length} onPrevious={goToPreviousDimensionShot} onNext={goToNextDimensionShot} className="h-full w-full" {...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops)} {...mainPhotoSizeProps} data-testid={PDP_MAIN_IMAGE} lazy={false} priority={true} onLoad={markLuxPageNavigationEnd} /> : !isBrowser && selectedPhoto?.src && !isDimensionImg ? <img className={classnames("h-full w-full object-contain object-center select-none", {
      "p-8": !isStudioGallery && !enableCrops,
      "scale-x-[-1]": !isStudioGallery && isConfigFlipped && !isDimensionImg,
      "hover:cursor-[unset]": !isStudioGallery && isCylindoConfigurationPhotos,
      "hover:cursor-zoom-in": mode === "pdp"
    })} src={ssrMainImageSrc || selectedPhoto.src} alt={productName ? String(productName) : ""} width={1600} height={2400} loading="eager" fetchPriority="high" decoding="async" data-testid={PDP_MAIN_IMAGE} onLoad={markLuxPageNavigationEnd} /> : <JBProductImage className={classnames("h-full w-full object-contain object-center select-none", {
      "p-8": !isStudioGallery && !enableCrops,
      "scale-x-[-1]": !isStudioGallery && isConfigFlipped && !isDimensionImg,
      "hover:cursor-[unset]": !isStudioGallery && isCylindoConfigurationPhotos,
      "hover:cursor-zoom-in": mode === "pdp"
    })} {...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops)} {...mainPhotoSizeProps} data-testid={PDP_MAIN_IMAGE} lazy={false} priority={true} onLoad={markLuxPageNavigationEnd} />);
    $[44] = currentDimensionShot;
    $[45] = dimensionShotUrls;
    $[46] = enableCrops;
    $[47] = goToNextDimensionShot;
    $[48] = goToPreviousDimensionShot;
    $[49] = hasMultipleDimensionShots;
    $[50] = isBrowser;
    $[51] = isConfigFlipped;
    $[52] = isCylindoAvailable;
    $[53] = isCylindoConfigurationPhotos;
    $[54] = isDimensionImg;
    $[55] = isStudioGallery;
    $[56] = isVideoSelected;
    $[57] = loading;
    $[58] = mainPhotoSizeProps;
    $[59] = mode;
    $[60] = productName;
    $[61] = selectedDimensionShotIndex;
    $[62] = selectedPhoto;
    $[63] = ssrMainImageSrc;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== introVideoId || $[66] !== isVideoSelected || $[67] !== loading) {
    t33 = !loading && isVideoSelected && introVideoId && <PDPProductIntroVideo videoId={introVideoId} />;
    $[65] = introVideoId;
    $[66] = isVideoSelected;
    $[67] = loading;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] !== cylindoFeatures || $[70] !== isConfigGallery || $[71] !== isCylindoAvailable || $[72] !== isCylindoConfigurationPhotos || $[73] !== isDimensionImg || $[74] !== isStudioGallery || $[75] !== isVideoSelected || $[76] !== loading || $[77] !== selectedPhotoIndex) {
    t34 = !loading && isCylindoAvailable && <div className={classnames("w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden", {
      invisible: !(isCylindoAvailable && isCylindoConfigurationPhotos) || isStudioGallery || isDimensionImg || isVideoSelected
    })}><PDPCylindo3dViewer cylindoFeatures={cylindoFeatures} rotateIndex={isConfigGallery ? selectedPhotoIndex : -1} /></div>;
    $[69] = cylindoFeatures;
    $[70] = isConfigGallery;
    $[71] = isCylindoAvailable;
    $[72] = isCylindoConfigurationPhotos;
    $[73] = isDimensionImg;
    $[74] = isStudioGallery;
    $[75] = isVideoSelected;
    $[76] = loading;
    $[77] = selectedPhotoIndex;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== isStudioGallery || $[80] !== isVideoSelected || $[81] !== loading || $[82] !== showDecideLaterOverlay) {
    t35 = !loading && showDecideLaterOverlay && !(isStudioGallery || isVideoSelected) && <PDPDecideLaterOverlay />;
    $[79] = isStudioGallery;
    $[80] = isVideoSelected;
    $[81] = loading;
    $[82] = showDecideLaterOverlay;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== loading) {
    t36 = loading && <Placeholder type="rect" loading={true} />;
    $[84] = loading;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== ratio || $[87] !== t30 || $[88] !== t31 || $[89] !== t32 || $[90] !== t33 || $[91] !== t34 || $[92] !== t35 || $[93] !== t36) {
    t37 = <RatioContainer className={t30} ratio={ratio} onClick={t31}>{t32}{t33}{t34}{t35}{t36}</RatioContainer>;
    $[86] = ratio;
    $[87] = t30;
    $[88] = t31;
    $[89] = t32;
    $[90] = t33;
    $[91] = t34;
    $[92] = t35;
    $[93] = t36;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t26 || $[96] !== t27 || $[97] !== t37) {
    t38 = <LinkOrFragment className={t26} to={t27}>{t37}</LinkOrFragment>;
    $[95] = t26;
    $[96] = t27;
    $[97] = t37;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  let t39;
  if ($[99] !== isBuildYourOwnImg || $[100] !== isStudioGallery || $[101] !== loading || $[102] !== pdpIsModularBYOProduct) {
    t39 = !loading && pdpIsModularBYOProduct && !isStudioGallery && !isBuildYourOwnImg ? <div className="text-sm font-bold bg-[rgba(44,44,44,.7)] text-white left-[calc(50%_-_85px)] opacity-60 absolute text-center top-[40%] w-[190px] z-[2]">Sample configuration shown</div> : null;
    $[99] = isBuildYourOwnImg;
    $[100] = isStudioGallery;
    $[101] = loading;
    $[102] = pdpIsModularBYOProduct;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  let t40;
  if ($[104] !== campaign) {
    t40 = campaign === "barbie" && <img src={BarbieLogo} alt="barbie-logo" className="w-20 h-20 absolute top-12 left-4 z-50" />;
    $[104] = campaign;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== campaign) {
    t41 = campaign === "bipoc" && <img src={BipocLogo} alt="bipoc-owned-logo" className="w-20 h-20 absolute top-12 left-4 z-50" />;
    $[106] = campaign;
    $[107] = t41;
  } else {
    t41 = $[107];
  }
  let t42;
  if ($[108] !== campaign) {
    t42 = campaign === "dazey" && <JBImage src={dazeyLogo} className="absolute top-10 left-2 z-50" width={120} />;
    $[108] = campaign;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  const t43 = loading || !isBrowser;
  let t44;
  if ($[110] !== configurationPhotos || $[111] !== dimensionShotUrls || $[112] !== dimensionsImage || $[113] !== enableCrops || $[114] !== introVideoId || $[115] !== isConfigFlipped || $[116] !== isCylindoConfigurationPhotos || $[117] !== isEditOrder || $[118] !== isMechanicalOpen || $[119] !== isVideoSelected || $[120] !== mode || $[121] !== pdpIsModularBYOProduct || $[122] !== productName || $[123] !== selectedPhoto || $[124] !== setIsVideoSelected || $[125] !== setSelectedPhoto || $[126] !== shouldShowDimensionThumbnail || $[127] !== studioPhotos || $[128] !== t43 || $[129] !== toggleMechanicalOpen || $[130] !== viewGalleryImageEvent) {
    t44 = <PDPDesktopThumbnails isEditOrder={isEditOrder} configurationPhotos={configurationPhotos} isMechanicalOpen={isMechanicalOpen} toggleMechanicalOpen={toggleMechanicalOpen} dimensionsImage={dimensionsImage} shouldShowDimensionThumbnail={shouldShowDimensionThumbnail} dimensionShotUrls={dimensionShotUrls} enableCrops={enableCrops} pdpIsModularBYOProduct={pdpIsModularBYOProduct} introVideoId={introVideoId} isConfigFlipped={isConfigFlipped} isCylindoConfigurationPhotos={isCylindoConfigurationPhotos} isVideoSelected={isVideoSelected} loading={t43} mode={mode} openModal={openGalleryModal} productName={productName} selectedPhoto={selectedPhoto} setSelectedPhoto={setSelectedPhoto} setIsVideoSelected={setIsVideoSelected} studioPhotos={studioPhotos} viewGalleryImageEvent={viewGalleryImageEvent} />;
    $[110] = configurationPhotos;
    $[111] = dimensionShotUrls;
    $[112] = dimensionsImage;
    $[113] = enableCrops;
    $[114] = introVideoId;
    $[115] = isConfigFlipped;
    $[116] = isCylindoConfigurationPhotos;
    $[117] = isEditOrder;
    $[118] = isMechanicalOpen;
    $[119] = isVideoSelected;
    $[120] = mode;
    $[121] = pdpIsModularBYOProduct;
    $[122] = productName;
    $[123] = selectedPhoto;
    $[124] = setIsVideoSelected;
    $[125] = setSelectedPhoto;
    $[126] = shouldShowDimensionThumbnail;
    $[127] = studioPhotos;
    $[128] = t43;
    $[129] = toggleMechanicalOpen;
    $[130] = viewGalleryImageEvent;
    $[131] = t44;
  } else {
    t44 = $[131];
  }
  let t45;
  if ($[132] !== isQuickviewMode || $[133] !== loading || $[134] !== mode) {
    t45 = !loading && <div className="flex justify-between left-4 absolute right-4 top-4 z-[2]"><PDPGalleryLabelsAndViewingCount mode={mode} />{!isQuickviewMode && <PDPFavoriteButton />}</div>;
    $[132] = isQuickviewMode;
    $[133] = loading;
    $[134] = mode;
    $[135] = t45;
  } else {
    t45 = $[135];
  }
  let t46;
  if ($[136] !== t25 || $[137] !== t38 || $[138] !== t39 || $[139] !== t40 || $[140] !== t41 || $[141] !== t42 || $[142] !== t44 || $[143] !== t45) {
    t46 = <div className="mb-8 ml-auto mr-auto relative" style={t25}>{t38}{t39}{t40}{t41}{t42}{t44}{t45}</div>;
    $[136] = t25;
    $[137] = t38;
    $[138] = t39;
    $[139] = t40;
    $[140] = t41;
    $[141] = t42;
    $[142] = t44;
    $[143] = t45;
    $[144] = t46;
  } else {
    t46 = $[144];
  }
  const t47 = productName || "";
  let t48;
  if ($[145] !== t47) {
    t48 = t47.split(" ").join("-");
    $[145] = t47;
    $[146] = t48;
  } else {
    t48 = $[146];
  }
  let t49;
  if ($[147] !== introVideoId || $[148] !== isEditOrder || $[149] !== isFullScreenGalleryOpen || $[150] !== isStudioGallery || $[151] !== isVideoSelected || $[152] !== pdpIsModularBYOProduct || $[153] !== photos || $[154] !== productName || $[155] !== selectedPhotoIndex || $[156] !== setIsVideoSelected || $[157] !== setSelectedPhoto || $[158] !== t48) {
    t49 = <BrowserOnly><PDPDesktopFullScreenGallery2 isEditOrder={isEditOrder} isOpen={isFullScreenGalleryOpen} photos={photos} initialSelectedPhotoIndex={selectedPhotoIndex} onClose={closeGalleryModal} isStudioGallery={isStudioGallery} pdpIsModularBYOProduct={pdpIsModularBYOProduct} productName={productName} key={t48} setSelectedPhoto={setSelectedPhoto} introVideoId={introVideoId} isVideoSelected={isVideoSelected} setIsVideoSelected={setIsVideoSelected} /></BrowserOnly>;
    $[147] = introVideoId;
    $[148] = isEditOrder;
    $[149] = isFullScreenGalleryOpen;
    $[150] = isStudioGallery;
    $[151] = isVideoSelected;
    $[152] = pdpIsModularBYOProduct;
    $[153] = photos;
    $[154] = productName;
    $[155] = selectedPhotoIndex;
    $[156] = setIsVideoSelected;
    $[157] = setSelectedPhoto;
    $[158] = t48;
    $[159] = t49;
  } else {
    t49 = $[159];
  }
  let t50;
  if ($[160] !== t46 || $[161] !== t49) {
    t50 = <>{t46}{t49}</>;
    $[160] = t46;
    $[161] = t49;
    $[162] = t50;
  } else {
    t50 = $[162];
  }
  return t50;
};
PDPDesktopHeroGalleryUI.propTypes = {
  configurationPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['buildyourown', 'configuration', 'cylindo', 'dimensional', 'studio'])
  })),
  isMechanicalOpen: PropTypes.bool,
  toggleMechanicalOpen: PropTypes.func,
  dimensionsImage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  }),
  isEditOrder: PropTypes.bool,
  enableCrops: PropTypes.bool,
  loading: PropTypes.bool,
  selectedPhoto: PropTypes.shape({
    type: PropTypes.oneOf(['buildyourown', 'configuration', 'cylindo', 'dimensional', 'studio']),
    src: PropTypes.string
  }),
  setSelectedPhoto: PropTypes.func,
  showDecideLaterOverlay: PropTypes.bool,
  studioPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired
  })),
  isConfigFlipped: PropTypes.bool,
  introVideoIds: PropTypes.shape({
    desktop: PropTypes.string,
    mobile: PropTypes.string
  }),
  setIsVideoSelected: PropTypes.func,
  isVideoSelected: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productName: PropTypes.string,
  isCylindoAvailable: PropTypes.bool,
  cylindoFeatures: PropTypes.arrayOf(PropTypes.string),
  isCylindoConfigurationPhotos: PropTypes.bool,
  viewGalleryImageEvent: PropTypes.func,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  mainPhotoLink: PropTypes.string,
  campaign: PropTypes.string
};
export default PDPDesktopHeroGalleryUI;